import { useState, useEffect } from 'react';
import { Brain, CheckCircle2, ChevronRight, Lock, Star, AlertCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import BackButton from '../components/BackButton';
import { useNavigate } from 'react-router-dom';
import { getModules } from '../mockApi';
import { api } from '../services/api';
import type { Module } from '../types';

export default function Quiz() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<'facile' | 'moyen' | 'difficile'>('facile');
  const [quizMode, setQuizMode] = useState<'progressive' | 'free'>('progressive');
  const [canTakeQuiz, setCanTakeQuiz] = useState<{ can_take: boolean; reason?: string } | null>(null);
  const [freeQuizRemaining, setFreeQuizRemaining] = useState<number>(3);
  const [freeQuizLimit, setFreeQuizLimit] = useState<number>(3);
  const [loading, setLoading] = useState(true);

  // Set default difficulty based on unlocked modes
  useEffect(() => {
    if (!user) return;

    console.log('User unlocked modes:', user?.unlocked_modes);
    const unlockedModes = user?.unlocked_modes || [];
    if (unlockedModes.includes('medium')) {
      console.log('Setting difficulty to moyen');
      setSelectedDifficulty('moyen');
    } else {
      console.log('Setting difficulty to facile');
      setSelectedDifficulty('facile');
    }
  }, [user?.unlocked_modes]); // Keep this dependency but add user check

  useEffect(() => {
    const loadQuizData = async () => {
      try {
        const [modules, quizPermission, freeQuizData] = await Promise.all([
          getModules(),
          user?.subscription ? api.canTakeQuiz() : Promise.resolve({ can_take: true }),
          !hasActiveSubscription() ?
            api.getFreeQuizCount().then(count => ({ remaining: count, limit: 3, is_limited: true }))
            : Promise.resolve({ remaining: 3, limit: 3, is_limited: false })
        ]);

        // Filter out VAE modules from quiz
        const regularModules = modules.filter(m => !m.id.startsWith('VAE-'));
        setModules(regularModules);
        // Select all modules by default for progressive mode
        setSelectedModules(regularModules.map(m => m.id));
        setCanTakeQuiz(quizPermission);
        setFreeQuizRemaining(freeQuizData.remaining || 3);
        setFreeQuizLimit(freeQuizData.limit || 3);
      } catch (error) {
        console.error('Error loading quiz data:', error);
        // Set fallback values
        setFreeQuizRemaining(3);
        setFreeQuizLimit(3);
      } finally {
        setLoading(false);
      }
    };

    loadQuizData();
  }, []); // Remove dependencies to prevent loops

  const unlockedModes = user?.unlocked_modes || [];
  const canAccessMedium = unlockedModes.includes('medium');
  const canAccessFree = unlockedModes.includes('free');

  const toggleModule = (moduleId: string) => {
    if (quizMode === 'progressive') return; // Can't change modules in progressive mode
    setSelectedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const startQuiz = () => {
    // Check free quiz limit for non-subscribed users
    if (!hasActiveSubscription() && freeQuizRemaining <= 0) {
      alert('Vous avez utilisé vos 3 QCM gratuits. Abonnez-vous pour continuer !');
      navigate('/tarifs');
      return;
    }

    // Check if user can take quiz (for SHY subscription)
    if (canTakeQuiz && !canTakeQuiz.can_take) {
      alert(canTakeQuiz.reason || 'Vous avez atteint votre limite de quiz pour aujourd\'hui.');
      return;
    }

    if (selectedModules.length === 0) {
      alert('Veuillez sélectionner au moins un module');
      return;
    }

    // Convert module IDs to module codes for API calls
    const moduleCodes = selectedModules.map(moduleId => {
      const module = modules.find(m => m.id === moduleId);
      return module?.code || moduleId;
    }).filter(code => code.startsWith('BC')); // Only BC modules for quiz

    navigate('/qcm/session', {
      state: {
        moduleIds: quizMode === 'free' ? moduleCodes : selectedModules,
        difficulty: selectedDifficulty,
        mode: quizMode
      }
    });
  };

  const getProgressiveDifficulties = () => {
    const difficulties = [
      {
        value: 'facile',
        label: 'Facile',
        description: '10 questions niveau facile',
        locked: false
      }
    ];

    if (canAccessMedium) {
      difficulties.push({
        value: 'moyen',
        label: 'Moyen',
        description: '4 faciles, 3 moyennes, 3 difficiles',
        locked: false
      });
    } else {
      difficulties.push({
        value: 'moyen',
        label: 'Moyen',
        description: 'Réussissez 10 QCM faciles à 100% pour débloquer',
        locked: true
      });
    }

    return difficulties;
  };

  const getFreeDifficulties = () => [
    { value: 'facile', label: 'Facile', description: 'Questions de base pour tester vos connaissances fondamentales' },
    { value: 'moyen', label: 'Mix équilibré', description: '4 faciles, 3 moyennes, 3 difficiles' },
    { value: 'difficile', label: 'Difficile', description: 'Questions avancées pour les experts du domaine' }
  ] as const;

  const isSHYSubscription = user?.subscription?.includes('SHY');

  // Check if user has active subscription
  const userHasActiveSubscription = user?.subscribed_until ? new Date(user.subscribed_until) > new Date() : false;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{ fontFamily: font.family }}>
        <div className="text-center">
          <div className={`w-12 h-12 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <BackButton />
        </div>
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Brain className={`h-8 w-8 text-${theme.text} mr-3`} />
            <h1 className={`text-3xl font-bold text-${theme.text}`}>
              QCM d'évaluation
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            {quizMode === 'progressive'
              ? 'Mode progressif : débloquez les niveaux en réussissant les QCM'
              : 'Mode libre : choisissez vos modules et niveau de difficulté'
            }
          </p>
        </div>

        {/* Free user quiz limit info */}
        {!hasActiveSubscription() && (
          <div className={`mb-8 p-6 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
            <div className="flex items-center">
              <Brain className={`h-5 w-5 text-${theme.text} mr-3`} />
              <div>
                <p className="text-gray-700">
                  <strong>Offre découverte :</strong> Il vous reste {freeQuizRemaining}/{freeQuizLimit} QCM gratuits.
                </p>
                {freeQuizRemaining <= 0 ? (
                  <p className="text-sm text-gray-600 mt-1">
                    Abonnez-vous pour des QCM illimités et accéder à tous les contenus !
                    <button
                      onClick={() => navigate('/tarifs')}
                      className={`ml-2 text-${theme.primary} hover:underline font-medium`}
                    >
                      Voir les formules
                    </button>
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 mt-1">
                    Profitez de vos QCM gratuits restants !
                    <button
                      onClick={() => navigate('/tarifs')}
                      className={`ml-2 text-${theme.primary} hover:underline font-medium`}
                    >
                      Débloquer l'accès complet
                    </button>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quiz Limit Warning for SHY subscription */}
        {hasActiveSubscription() && isSHYSubscription && canTakeQuiz && !canTakeQuiz.can_take && (
          <div className="mb-8 p-6 rounded-lg bg-red-50 border border-red-200">
            <div className="flex items-start">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800 mb-2">Limite quotidienne atteinte</h3>
                <p className="text-red-700 mb-4">
                  {canTakeQuiz.reason || 'Vous avez déjà effectué votre quiz quotidien. Revenez demain pour continuer votre progression !'}
                </p>
                <button
                  onClick={() => navigate('/tarifs?upgrade=true')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Passer à une formule supérieure
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Limit Info for SHY subscription */}
        {isSHYSubscription && userHasActiveSubscription && canTakeQuiz && canTakeQuiz.can_take && (
          <div className={`mb-8 p-4 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
            <div className="flex items-center">
              <AlertCircle className={`h-5 w-5 text-${theme.text} mr-3`} />
              <p className="text-gray-700">
                <strong>Formule Timide :</strong> Vous pouvez effectuer 1 quiz par jour.
                <button
                  onClick={() => navigate('/tarifs?upgrade=true')}
                  className={`ml-2 text-${theme.primary} hover:underline font-medium`}
                >
                  Passer à une formule supérieure pour des quiz illimités
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Mode Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Mode de jeu</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setQuizMode('progressive')}
              className={`
                w-full text-left p-6 rounded-lg border-2 transition-all duration-200
                ${quizMode === 'progressive'
                  ? `border-${theme.primary} bg-${theme.secondary}`
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center">
                  <Star className="h-5 w-5 mr-2" />
                  Mode Progressif
                </h3>
                {quizMode === 'progressive' && (
                  <CheckCircle2 className={`h-5 w-5 text-${theme.primary}`} />
                )}
              </div>
              <p className="text-sm text-gray-600">Débloquez les niveaux progressivement en réussissant les QCM</p>
            </button>

            <button
              onClick={() => canAccessFree && setQuizMode('free')}
              disabled={!canAccessFree}
              className={`
                w-full text-left p-6 rounded-lg border-2 transition-all duration-200 relative
                ${!canAccessFree
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  : quizMode === 'free'
                    ? `border-${theme.primary} bg-${theme.secondary}`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center">
                  {!canAccessFree && <Lock className="h-5 w-5 mr-2 text-gray-400" />}
                  Mode Libre
                </h3>
                {quizMode === 'free' && canAccessFree && (
                  <CheckCircle2 className={`h-5 w-5 text-${theme.primary}`} />
                )}
              </div>
              <p className="text-sm text-gray-600">
                {canAccessFree
                  ? 'Choisissez librement vos modules et niveau de difficulté'
                  : 'Réussissez 10 QCM moyens à 100% pour débloquer'
                }
              </p>
            </button>
          </div>
        </div>
        {/* Difficulty Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Niveau de difficulté</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(quizMode === 'progressive' ? getProgressiveDifficulties() : getFreeDifficulties()).map(({ value, label, description, locked }) => (
              <button
                key={value}
                onClick={() => !locked && setSelectedDifficulty(value)}
                disabled={locked}
                className={`
                  w-full text-left p-6 rounded-lg border-2 transition-all duration-200 relative
                  ${locked
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : selectedDifficulty === value
                      ? `border-${theme.primary} bg-${theme.secondary}`
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center">
                    {locked && <Lock className="h-5 w-5 mr-2 text-gray-400" />}
                    {label}
                  </h3>
                  {selectedDifficulty === value && !locked && (
                    <CheckCircle2 className={`h-5 w-5 text-${theme.primary}`} />
                  )}
                </div>
                <p className="text-sm text-gray-600">{description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Module Selection (only in free mode) */}
        {quizMode === 'free' && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Sélection des modules</h2>
            <div className="grid gap-4">
              {modules.map((module) => (
                <button
                  key={module.id}
                  onClick={() => toggleModule(module.id)}
                  className={`
                    w-full text-left p-6 rounded-lg border-2 transition-all duration-200
                    ${selectedModules.includes(module.id)
                      ? `border-${theme.primary} bg-${theme.secondary}`
                      : 'border-gray-200 bg-white hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className={`text-${theme.text} font-semibold mr-2`}>
                          {module.code}
                        </span>
                        <h3 className="font-semibold break-words">{module.title}</h3>
                      </div>
                    </div>
                    <div className="ml-4">
                      {selectedModules.includes(module.id) ? (
                        <CheckCircle2 className={`h-6 w-6 text-${theme.primary}`} />
                      ) : (
                        <div className={`h-6 w-6 rounded-full border-2 border-gray-300`} />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Progressive Mode Info */}
        {quizMode === 'progressive' && (
          <div className={`mb-8 p-6 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
            <h3 className="font-semibold mb-2">Mode progressif activé</h3>
            <p className="text-gray-600 mb-4">
              Tous les modules sont automatiquement sélectionnés. Progressez étape par étape :
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center">
                <div className={`w-2 h-2 rounded-full bg-${theme.primary} mr-3`} />
                Réussissez 10 QCM faciles à 100% pour débloquer le niveau moyen
              </li>
              <li className="flex items-center">
                <div className={`w-2 h-2 rounded-full ${canAccessMedium ? `bg-${theme.primary}` : 'bg-gray-300'} mr-3`} />
                Réussissez 10 QCM moyens à 100% pour débloquer le mode libre
              </li>
            </ul>
          </div>
        )}

        {/* Module display for progressive mode */}
        {quizMode === 'progressive' && (
          <div className="grid gap-4 mb-8">
          {modules.map((module) => (
            <div
              key={module.id}
              className={`w-full text-left p-6 rounded-lg border-2 border-${theme.primary} bg-${theme.secondary}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className={`text-${theme.text} font-semibold mr-2`}>
                      {module.code}
                    </span>
                    <h3 className="font-semibold break-words">{module.title}</h3>
                  </div>
                </div>
                <div className="ml-4">
                  <CheckCircle2 className={`h-6 w-6 text-${theme.primary}`} />
                </div>
              </div>
            </div>
          ))}
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={startQuiz}
            disabled={
              selectedModules.length === 0 ||
              (quizMode === 'progressive' && selectedDifficulty === 'moyen' && !canAccessMedium) ||
              (canTakeQuiz && !canTakeQuiz.can_take)
            }
            className={`
              flex items-center px-8 py-4 rounded-lg text-lg font-medium
              transition-all duration-200
              ${selectedModules.length > 0 && 
                !(quizMode === 'progressive' && selectedDifficulty === 'moyen' && !canAccessMedium) &&
                !(canTakeQuiz && !canTakeQuiz.can_take)
                ? `bg-${theme.primary} text-white hover:opacity-90`
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {canTakeQuiz && !canTakeQuiz.can_take
              ? 'Limite quotidienne atteinte'
              : 'Commencer l\'évaluation'
            }
            <ChevronRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}